/**
 * AdminEditableCard — generic admin-only long-press wrapper for any sellable
 * content card (wallpapers, live wallpapers, ringtones).
 *
 * Usage:
 *   <AdminEditableCard item={wallpaper}>
 *     <RingtoneRow ... />
 *   </AdminEditableCard>
 *
 * - Long-press (550 ms, mouse + touch) opens a portal popup with Edit/Delete.
 * - The popup uses createPortal to render directly in document.body, bypassing
 *   any transform stacking context from framer-motion or fixed modals.
 * - Modal title and labels adapt to the item's mediaType.
 * - Non-admins see zero change — children render as-is.
 * - Uses updateWallpapers / deleteWallpapers from the wallpapers collection.
 */

import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Pencil, Trash2 } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPortal,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLongPress } from '@/hooks/use-long-press';
import { useIsAdmin } from '@/hooks/use-is-admin';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';
import { updateWallpapers } from '@/lib/collections/wallpapers';
import { Time } from '@/lib/db-client';
import { SkrTokenIcon } from '@/components/SkrTokenIcon';
import { SKR_MIN_PRICE_RAW } from '@/lib/constants';
import { useWallpaperCategoriesContext } from '@/contexts/WallpaperCategoriesContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const RAW_PER_SKR = 1_000_000;
const MIN_PRICE_RAW = Number(SKR_MIN_PRICE_RAW);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRawPrice(rawPrice?: number | null): string {
  return ((rawPrice ?? MIN_PRICE_RAW) / RAW_PER_SKR).toString();
}

function parseSkrToRaw(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d+(?:\.\d{0,6})?$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * RAW_PER_SKR);
}

/** Human-readable label based on mediaType */
function getItemLabel(mediaType?: string | null): string {
  if (mediaType === 'live-wallpaper') return 'live wallpaper';
  if (mediaType === 'ringtone') return 'ringtone';
  return 'wallpaper';
}

// ─── Portal Popup (Edit / Delete context menu) ────────────────────────────────

interface AdminPopupProps {
  x: number;
  y: number;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  hideDelete?: boolean;
}

function AdminPopup({ x, y, onEdit, onDelete, onClose, hideDelete }: AdminPopupProps) {
  const left = Math.min(x, window.innerWidth - 168);
  const top = Math.min(y + 10, window.innerHeight - 110);

  return createPortal(
    <>
      {/* Full-screen dismiss backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 10002 }}
        onClick={onClose}
      />
      {/* Context menu card */}
      <div
        style={{
          position: 'fixed',
          left,
          top,
          zIndex: 10003,
          background: '#1a1a2e',
          border: '1px solid rgba(139,92,246,0.35)',
          borderRadius: 14,
          padding: 6,
          minWidth: 152,
          boxShadow: '0 8px 32px rgba(0,0,0,0.65), 0 0 0 1px rgba(139,92,246,0.12)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          animation: 'adminPopupIn 0.15s ease',
        }}
      >
        <style>{`
          @keyframes adminPopupIn {
            from { opacity: 0; transform: scale(0.9) translateY(-4px); }
            to   { opacity: 1; transform: scale(1)   translateY(0); }
          }
        `}</style>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 9,
            padding: '8px 11px', borderRadius: 9, border: 'none',
            background: 'transparent', cursor: 'pointer',
            color: '#f0f0f5', fontSize: 13, fontWeight: 600,
            fontFamily: "'Syne', sans-serif", textAlign: 'left',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(139,92,246,0.15)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <Pencil size={14} color="#a78bfa" />
          Edit
        </button>
        {!hideDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 9,
              padding: '8px 11px', borderRadius: 9, border: 'none',
              background: 'transparent', cursor: 'pointer',
              color: 'rgba(248,113,113,0.9)', fontSize: 13, fontWeight: 600,
              fontFamily: "'Syne', sans-serif", textAlign: 'left',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.12)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Trash2 size={14} color="rgba(248,113,113,0.85)" />
            Delete
          </button>
        )}
      </div>
    </>,
    document.body,
  );
}

// ─── Edit + Delete Modal ──────────────────────────────────────────────────────

interface AdminEditModalProps {
  open: boolean;
  onClose: () => void;
  item: WallpapersResponse;
  onSaved?: () => void;
}

function AdminEditModal({ open, onClose, item, onSaved }: AdminEditModalProps) {
  const [titleInput, setTitleInput] = useState(item.title ?? '');
  const [categoryInput, setCategoryInput] = useState(item.category ?? '');
  const [priceInput, setPriceInput] = useState(() => formatRawPrice(item.price));
  const [saving, setSaving] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const mediaType = (item as WallpapersResponse & { mediaType?: string }).mediaType;
  const label = getItemLabel(mediaType);

  // Admin-managed category lists — read from shared context (single subscription for the whole app)
  const { wallpaperCats, liveCats, ringtoneCats } = useWallpaperCategoriesContext();
  const adminCats = mediaType === 'ringtone' ? ringtoneCats : mediaType === 'live-wallpaper' ? liveCats : wallpaperCats;

  // Keep fields fresh when the dialog opens or item changes
  React.useEffect(() => {
    if (open) {
      setTitleInput(item.title ?? '');
      setCategoryInput(item.category ?? '');
      setPriceInput(formatRawPrice(item.price));
      setDeleteConfirmOpen(false);
    }
  }, [open, item.id, item.title, item.category, item.price]);

  const priceError = useMemo(() => {
    const raw = parseSkrToRaw(priceInput);
    if (raw === null) return 'Enter a valid SKR amount (up to 6 decimals).';
    if (raw < MIN_PRICE_RAW) return 'Minimum price is 1 SKR.';
    return null;
  }, [priceInput]);

  const titleError = titleInput.trim().length === 0 ? 'Title cannot be empty.' : null;
  const hasError = !!priceError || !!titleError;

  const handleSave = async () => {
    if (hasError) return;
    const nextRaw = parseSkrToRaw(priceInput);
    if (nextRaw === null || nextRaw < MIN_PRICE_RAW) {
      toast.error(priceError ?? 'Invalid price');
      return;
    }
    setSaving(true);
    const categoryChanged = (categoryInput || undefined) !== (item.category || undefined);
    const ok = await updateWallpapers(item.id, {
      title: titleInput.trim(),
      category: categoryInput || undefined,
      price: nextRaw,
      ...(categoryChanged ? { categorySetAt: Time.Now } : {}),
    });
    setSaving(false);

    if (ok) {
      onSaved?.();
      onClose();
    } else {
      toast.error(`Failed to update ${label} — check permissions`);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const ok = await updateWallpapers(item.id, { status: 'deleted' });
    setDeleting(false);
    if (ok) {
      setDeleteConfirmOpen(false);
      onClose();
    } else {
      toast.error(`Failed to delete ${label}`);
    }
  };

  const isKnownCategory = adminCats.includes(categoryInput);
  const selectValue = isKnownCategory ? categoryInput : (categoryInput ? '__orphan__' : '__none__');

  if (deleteConfirmOpen) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setDeleteConfirmOpen(false); onClose(); } }}>
        <DialogPortal>
          {/* Solid black backdrop */}
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-[10000] bg-black data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-[#11111b] border-red-500/20 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg sm:max-w-[420px] text-white"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="font-['Syne',sans-serif] text-lg text-red-100">
                Delete {label}?
              </DialogTitle>
              <DialogDescription className="text-white/55">
                Are you sure? Users who purchased this {label} will keep it, but it will be removed from the store and favorites.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                className="border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : `Delete ${label.charAt(0).toUpperCase() + label.slice(1)}`}
              </Button>
            </DialogFooter>
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
              <span className="h-4 w-4 text-white/60 text-sm">✕</span>
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPortal>
        {/* Solid black backdrop */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[10000] bg-black data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-[#11111b] border-white/10 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg sm:max-w-[440px] text-white"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-['Syne',sans-serif] text-lg">
              Edit {label}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Admin-only. Changes apply immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {/* Title */}
            <div className="grid gap-1.5">
              <label className="text-xs uppercase tracking-[0.18em] text-white/45">
                Title
              </label>
              <Input
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder={`${label.charAt(0).toUpperCase() + label.slice(1)} title`}
                className="border-white/10 bg-white/5 text-white placeholder:text-white/25"
              />
              {titleError && (
                <p className="text-xs text-red-300">{titleError}</p>
              )}
            </div>

            {/* Category — dropdown only, sourced from admin-managed lists */}
            <div className="grid gap-1.5">
              <label className="text-xs uppercase tracking-[0.18em] text-white/45">
                Category
              </label>
              <Select
                value={selectValue}
                onValueChange={(val) => {
                  if (val === '__orphan__') return;
                  setCategoryInput(val === '__none__' ? '' : val);
                }}
              >
                <SelectTrigger className="border-white/10 bg-white/5 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#1a1a2e] text-white">
                  <SelectItem value="__none__" className="text-white/45 focus:text-white">
                    — None —
                  </SelectItem>
                  {adminCats.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-white focus:text-white">
                      {cat}
                    </SelectItem>
                  ))}
                  {!isKnownCategory && categoryInput && (
                    <SelectItem value="__orphan__" className="text-white/45 focus:text-white/45">
                      {categoryInput} (current)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Price */}
            <div className="grid gap-1.5">
              <label className="text-xs uppercase tracking-[0.18em] text-white/45">
                Price (SKR)
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                  <SkrTokenIcon size={14} />
                </span>
                <Input
                  inputMode="decimal"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="1.0"
                  className="border-white/10 bg-white/5 pl-8 text-white placeholder:text-white/25"
                />
              </div>
              <p className={`text-xs ${priceError ? 'text-red-300' : 'text-white/40'}`}>
                {priceError ?? 'Minimum price: 1 SKR.'}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={hasError || saving}
              className="bg-violet-500 text-white hover:bg-violet-400"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <span className="h-4 w-4 text-white/60 text-sm">✕</span>
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

// ─── AdminEditableCard wrapper ────────────────────────────────────────────────

interface AdminEditableCardProps {
  /** The content item — must be a WallpapersResponse (all types live in wallpapers collection) */
  item: WallpapersResponse;
  /** Optional callback after a successful save */
  onSaved?: () => void;
  /** Card content rendered inside the wrapper */
  children: React.ReactNode;
  /** Extra className forwarded to the wrapper div */
  className?: string;
  style?: React.CSSProperties;
  /**
   * When true, the Delete option is hidden from the context menu.
   * Use this in library/trash contexts where the item should not be
   * permanently removed from the public catalog.
   */
  hideDelete?: boolean;
}

/**
 * Wraps any sellable card (wallpaper, live wallpaper, or ringtone row).
 * Admins trigger a long-press (mouse or touch, ~550 ms) to open a portal popup
 * with Edit and Delete options.
 *
 * The popup uses createPortal so it renders in document.body — it is never
 * clipped or offset by transform stacking contexts from framer-motion or
 * position:fixed modal containers.
 *
 * Non-admins are completely unaffected — children render as-is with no overhead.
 */
export function AdminEditableCard({
  item,
  onSaved,
  children,
  className,
  style,
  hideDelete,
}: AdminEditableCardProps) {
  const isAdmin = useIsAdmin();
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const suppressClickRef = React.useRef(false);

  const lp = useLongPress({
    delay: 550,
    enabled: isAdmin,
    onLongPress: (x, y) => {
      suppressClickRef.current = true;
      setMenuPos({ x, y });
    },
  });

  // Non-admins: zero overhead, render children as-is
  if (!isAdmin) {
    return <>{children}</>;
  }

  const closeMenu = () => setMenuPos(null);

  const handleClick = (e: React.MouseEvent) => {
    if (suppressClickRef.current || lp.triggered()) {
      e.stopPropagation();
      suppressClickRef.current = false;
      lp.clearTriggered();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    suppressClickRef.current = true;
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <div
        className={className}
        style={{ ...style, position: 'relative' }}
        onMouseDown={lp.onMouseDown}
        onMouseUp={lp.onMouseUp}
        onMouseMove={lp.onMouseMove}
        onMouseLeave={lp.onMouseLeave}
        onTouchStart={lp.onTouchStart}
        onTouchEnd={lp.onTouchEnd}
        onTouchCancel={lp.onTouchCancel}
        onTouchMove={lp.onTouchMove}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {children}
      </div>

      {/* Portal popup — renders in document.body, bypasses all stacking contexts */}
      {menuPos && (
        <AdminPopup
          x={menuPos.x}
          y={menuPos.y}
          hideDelete={hideDelete}
          onClose={() => {
            closeMenu();
            suppressClickRef.current = false;
            lp.clearTriggered();
          }}
          onEdit={() => {
            closeMenu();
            setEditOpen(true);
          }}
          onDelete={() => {
            closeMenu();
            setDeleteOpen(true);
          }}
        />
      )}

      <AdminEditModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          suppressClickRef.current = false;
          lp.clearTriggered();
        }}
        item={item}
        onSaved={onSaved}
      />

      {/* Standalone delete confirm dialog */}
      {deleteOpen && (
        <AdminDeleteConfirm
          open={deleteOpen}
          item={item}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </>
  );
}

// ─── Standalone delete confirm (used by AdminEditableCard direct delete) ──────

interface AdminDeleteConfirmProps {
  open: boolean;
  item: WallpapersResponse;
  onClose: () => void;
}

function AdminDeleteConfirm({ open, item, onClose }: AdminDeleteConfirmProps) {
  const [deleting, setDeleting] = useState(false);
  const label = getItemLabel((item as WallpapersResponse & { mediaType?: string }).mediaType);

  const handleDelete = async () => {
    setDeleting(true);
    const ok = await updateWallpapers(item.id, { status: 'deleted' });
    setDeleting(false);
    if (ok) {
      onClose();
    } else {
      toast.error(`Failed to delete ${label}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[10000] bg-black data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-[#11111b] border-red-500/20 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.6)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg sm:max-w-[420px] text-white"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-['Syne',sans-serif] text-lg text-red-100">
              Delete {label}?
            </DialogTitle>
            <DialogDescription className="text-white/55">
              Are you sure? Users who purchased this {label} will keep it, but it will be removed from the store and favorites.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : `Delete ${label.charAt(0).toUpperCase() + label.slice(1)}`}
            </Button>
          </DialogFooter>
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <span className="h-4 w-4 text-white/60 text-sm">✕</span>
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export default AdminEditableCard;
